/**
 * The dashboard's primary navigation model.
 *
 * ## Grouped by the job, not by the system's object graph
 *
 * The sections used to be named after the platform's own layers — Explore /
 * Analyze / Automate / Settings — which meant the rail described how the product
 * is BUILT rather than what an analyst is doing. Graphs, Flows, Explorer,
 * Activity, Actors, Situations, Watches, Tracers and Grouping rules were 26 flat
 * destinations whose overlap could only be learned by opening each one.
 *
 * The sections now name the work: {@link NAV_SECTIONS}. Today is the shift
 * you are on (what fired, what has movement, what you are watching);
 * Investigate is the evidence you open when something needs answering;
 * Reference is the material you consult rather than work in; Automate is what
 * runs without you; Account is you and your workspace. Nothing was merged or
 * renamed at the route level — every destination that was reachable still is.
 *
 * Each item carries the RBAC `resource`/`action` it gates on, and BOTH surfaces
 * that render this table (the sidebar and the command palette) filter through
 * the one helper — {@link visibleNavItems} — so a regroup cannot widen what an
 * entry shows. Icons come from lucide-react (ui-core's icon set).
 */
import {
  Braces,
  ClipboardCheck,
  Compass,
  LayoutDashboard,
  ArrowLeftRight,
  Wallet,
  WalletCards,
  Network,
  Share2,
  Microscope,
  FileCode2,
  BarChart3,
  LineChart,
  Table2,
  Activity,
  Workflow,
  Play,
  Waypoints,
  Combine,
  BellRing,
  Eye,
  Radar,
  Users,
  Settings,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { RESOURCES, ROUTES } from "./constants";

/**
 * Section headings, in rail order — the SINGLE source of both the names and the
 * order. The sidebar iterates this; it does not carry a second list.
 *
 * Declared rather than derived from `NAV_ITEMS` so the names are a closed set
 * the item table type-checks against (a typo used to mint a silent new section),
 * and so the collapse defaults below can name one. `navigation.test.ts` asserts
 * this order still matches the order the items are written in.
 */
export const NAV_SECTIONS = [
  "Today",
  "Investigate",
  "Reference",
  "Automate",
  "Account",
] as const;

export type NavSection = (typeof NAV_SECTIONS)[number];

/**
 * Sections that fold away, and start folded.
 *
 * The rail is ~24 entries expanded, which reads as a wall rather than a list.
 * These two are the low-traffic ones — Reference is consulted, not worked in,
 * and Automate is what runs without you — so collapsing them by default puts
 * roughly fourteen entries on screen while keeping both one click away. The
 * sidebar overrides the collapse for whichever section holds the ACTIVE route:
 * hiding where the user is standing is never the right trade.
 */
export const COLLAPSIBLE_NAV_SECTIONS: readonly NavSection[] = ["Reference", "Automate"];

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** RBAC resource gating visibility (defaults to always-visible when omitted). */
  resource?: string;
  /** RBAC action (defaults to `list`). */
  action?: string;
  /**
   * Plan entitlement this entry is PACKAGED behind, from `ENTITLEMENT` in
   * `@authe/fe-commons/plans` — a different question from `resource`.
   *
   * `resource` asks "may this person"; an administrator in their own org can
   * grant it. This asks "does this customer's tier include it", which only a
   * subscription change fixes. Omitted on every entry that is part of the
   * product rather than part of a package, which is almost all of them.
   *
   * Withholding it hides the entry; the API refuses the call
   * (`commons.authz.require_entitlements`). A gate that exists only here is a
   * suggestion.
   */
  capability?: string;
  /**
   * The rail section this entry occupies, or `null` for a destination that is
   * navigable and searchable but holds no permanent rail slot.
   *
   * Required (not optional) so every entry has to answer: an omitted section
   * silently drops an entry out of the sidebar, which is how a page comes to be
   * routed, built and unreachable.
   */
  section: NavSection | null;
}

export const NAV_ITEMS: NavItem[] = [
  // ── Today ──────────────────────────────────────────────────────────────────
  // The landing route, labelled for what it is being rebuilt as: the analyst's
  // queue — alerts that fired, cases with activity, live watches. "Overview" named
  // a layout, not a job, and put the one route everybody lands on last in the
  // reading order of a section called "Explore".
  { to: ROUTES.overview, label: "Queue", icon: LayoutDashboard, resource: RESOURCES.overview, section: "Today" },
  // Investigations are a client-side collaborative workspace (no backend RBAC
  // resource yet), so — like Wallet — the entry is intentionally ungated.
  { to: ROUTES.investigations, label: "Investigations", icon: Microscope, section: "Today" },
  { to: ROUTES.alerts, label: "Alerts", icon: BellRing, resource: RESOURCES.alerts, action: "read", section: "Today" },
  { to: ROUTES.watches, label: "Watches", icon: Eye, resource: RESOURCES.watches, action: "read", section: "Today" },
  // What the watches are watching FOR, already detected and grouped. Gated on
  // `watches` rather than `pipelines`: the rows come from pipeline-mgmt-api, but
  // reading the platform's own detections is the reason a customer holds a watch
  // permission at all, and gating it on pipelines would hide it from every
  // tenant who does not author pipelines — which is most of them.
  { to: ROUTES.situations, label: "Situations", icon: Radar, resource: RESOURCES.watches, action: "read", section: "Today" },

  // ── Investigate ────────────────────────────────────────────────────────────
  { to: ROUTES.transactions, label: "Transactions", icon: ArrowLeftRight, resource: RESOURCES.transactions, section: "Investigate" },
  { to: ROUTES.addresses, label: "Addresses", icon: Wallet, resource: RESOURCES.addresses, section: "Investigate" },
  { to: ROUTES.graphs, label: "Graphs", icon: Share2, resource: RESOURCES.graphs, section: "Investigate" },
  // The platform's own detections re-keyed on WHERE THE MONEY WENT — an
  // investigative lens, beside Graphs rather than filed with the machinery that
  // produced it. Still on `watches`, the resource whose detections it reads.
  { to: ROUTES.flows, label: "Flows", icon: Waypoints, resource: RESOURCES.watches, action: "read", section: "Investigate" },
  // Gated on `abis`, not `contracts`: the page's only read is `GET /abis`, which
  // the authorizer enforces as `abis:list` — so that is the grant that decides
  // whether this entry leads anywhere. `contracts` was never wired to anything
  // on the backend, and gating on it meant the entry's visibility and the page's
  // behaviour answered to two different permissions.
  { to: ROUTES.contracts, label: "Contracts", icon: FileCode2, resource: RESOURCES.abis, section: "Investigate" },
  { to: ROUTES.activity, label: "Activity", icon: Activity, resource: RESOURCES.activity, section: "Investigate" },

  // ── Reference ──────────────────────────────────────────────────────────────
  // One entry for what used to be "Entities" and "Groups": the same catalog,
  // split by kind rather than by page. Still gated on `entities` — the two
  // resources have never been granted apart, and gating on the narrower of them
  // would hide groups from someone entitled to see them.
  { to: ROUTES.catalog, label: "Catalog", icon: Network, resource: RESOURCES.entities, section: "Reference" },
  // The other half of the catalog: everything the platform DISCOVERED and
  // quarantined instead of writing. It sits beside Catalog because that is what
  // it is about — a proposal nobody approves is a catalog row that never exists,
  // and the queue was reachable only from a CLI.
  //
  // Gated on `seed:read` — the scope the route's own authorizer enforces, now
  // that `scopes/dashboard.json` mints the family (it used to stand in on
  // `catalog:read`, which passed the gate while the route still 403'd). `read`,
  // not the default `list` — seed-mgmt-api's family is read/review.
  { to: ROUTES.proposals, label: "Proposals", icon: ClipboardCheck, resource: RESOURCES.seed, action: "read", section: "Reference" },
  // Wallets ranked by conduct and realised ETH. Gated on the analytics read
  // rather than on `watches` because that is what it IS: the same decoded facts,
  // aggregated by address through the metadata-registry views. Situations reads
  // detections from pipeline-mgmt-api; this reads the warehouse.
  { to: ROUTES.actors, label: "Actors", icon: Radar, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Reference" },
  // How transactions are decoded — the definition catalogue + the tenant's own
  // `definition_patch` overrides — AND how sure the platform has to be before
  // acting on the result (the per-scope confidence policy, which lives on the
  // same page). Named for both: the policy governs every judgement on the
  // platform, and nobody looking for it would have guessed it was filed under a
  // label that only mentions decoding. Same resource, read with `analytics`;
  // authoring is gated per-control by `edit` / `create`.
  { to: ROUTES.analyticsProtocols, label: "Decoding & confidence", icon: Braces, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Reference" },
  // Gated on the analytics read, NOT on the `stats:*` family the route is named
  // for. The page's panels are metadata-registry reads, so `stats:list` was a
  // gate over a permission the page no longer needs — it would have shown the
  // entry to someone who could only reach a permission wall, and hidden it from
  // someone entitled to every number on it. Same gate as the two entries below,
  // which read the same data.
  { to: ROUTES.stats, label: "Stats", icon: BarChart3, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Reference" },
  // Analytics reads over the metadata-registry (decoded-fact EAV + views). Gated
  // on the read verb the Reader tier carries (`metadata-registry:analytics`).
  { to: ROUTES.analytics, label: "Analytics", icon: LineChart, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Reference" },
  // Datadog-style transaction explorer over the same decoded-metadata reads;
  // shares the `metadata-registry:analytics` read verb.
  { to: ROUTES.analyticsExplorer, label: "Explorer", icon: Table2, resource: RESOURCES.metadataRegistry, action: "analytics", section: "Reference" },

  // ── Automate ───────────────────────────────────────────────────────────────
  { to: ROUTES.pipelines, label: "Pipelines", icon: Workflow, resource: RESOURCES.pipelines, action: "read", section: "Automate" },
  // Gated on the permission the page's own READ needs, not the one its Start
  // button needs. `GET /runs` is tagged `scope:pipelines:read`; only `ANY /runs`
  // — starting one — is tagged `scope:pipelines:process`, and `:process` is in
  // neither `READ_SUFFIXES` nor `WRITE_SUFFIXES` (commons `permissions/_codegen`),
  // so it lands in the admin-only remainder. Gating the ENTRY on it meant a
  // Writer never saw "Runs" in the rail and could not find it in the command
  // palette, while the API would have served them the whole run history.
  //
  // Nothing is opened up: "Start run" is separately gated on
  // `can(RESOURCES.runs, "process")` inside the page.
  { to: ROUTES.runs, label: "Runs", icon: Play, resource: RESOURCES.runs, action: "read", section: "Automate" },
  { to: ROUTES.tracers, label: "Tracers", icon: Waypoints, resource: RESOURCES.tracers, action: "read", section: "Automate" },
  // Configuration, so it sits with Pipelines and Tracers — the same split the
  // route itself is commented with (Runs is the operational record; these say
  // what should happen). It had no entry at all until recently: the page was
  // routed, built and reachable only by typing the URL, which is how a laddered
  // config surface comes to look like it does not exist.
  //
  // Gated on `pipelines:read` like Pipelines, because that is the permission the
  // rules' own CRUD is gated on — authoring `system` scope additionally needs
  // `pipelines:admin`, which the page enforces itself.
  { to: ROUTES.groupingRules, label: "Grouping rules", icon: Combine, resource: RESOURCES.pipelines, action: "read", section: "Automate" },

  // ── Account ────────────────────────────────────────────────────────────────
  // Wallet is a purely client-side feature (browser wallet via wagmi) with no
  // backend `wallets` RBAC resource, so it is intentionally NOT gated — every
  // signed-in user can connect/inspect their wallet.
  { to: ROUTES.wallet, label: "Wallet", icon: WalletCards, section: "Account" },
  { to: ROUTES.team, label: "Team", icon: Users, resource: RESOURCES.team, section: "Account" },
  { to: ROUTES.settings, label: "Settings", icon: Settings, resource: RESOURCES.settings, section: "Account" },

  // ── Off the rail ───────────────────────────────────────────────────────────
  // Onboarding, so it is read once and never again — and it was costing a
  // permanent rail slot for it. It is already offered by the landing page and by
  // `?guide=1`, and it now also lives in the profile menu beside "Your exports",
  // which is where the things-about-you-not-about-the-data live. It stays in
  // this table (with no section) so the command palette can still find it by
  // name: off the rail is not the same as unreachable. Ungated — every
  // signed-in user can follow setup.
  { to: ROUTES.gettingStarted, label: "Getting Started", icon: Compass, section: null },
  // Ask AI is a DRAWER, not a destination — the header's Sparkles button opens
  // it on every route, which is why it has no rail slot. But "no rail slot" was
  // read as "not in this table", and the palette only searches this table, so
  // typing "ask" returned nothing and the sole remaining door was an icon-only
  // button with no visible name. Listed here for the same reason Getting
  // Started is: the palette is the other half of the navigation, and a feature
  // it cannot name is a feature the keyboard cannot reach. `/ask-ai` opens the
  // drawer and bounces back, so selecting it does what the button does.
  { to: ROUTES.askAi, label: "Ask AI", icon: Sparkles, section: null },
];

/** Can this session see `item`? The ONE gate both nav surfaces filter through. */
export function navItemVisible(
  item: NavItem,
  can: (resource: string, action: string) => boolean,
  entitled: (capability: string | undefined) => boolean = () => true,
): boolean {
  return (
    (!item.resource || can(item.resource, item.action ?? "list")) &&
    // Symmetric with the resource gate above: an entry that is not packaged is
    // never put to the predicate at all, so no predicate can hide the product
    // itself — only the entries that name a capability.
    (!item.capability || entitled(item.capability))
  );
}

/**
 * The default entitlement predicate: everything is included.
 *
 * So a caller that has no plan context — a test, a host with no billing model,
 * the first frames before the subscription read lands — sees exactly the rail it
 * saw before packaging existed. The inverse default would hide paid features
 * from paying customers for the first 200ms of every session, which is the one
 * failure mode a ceiling must never have.
 */
const ENTITLED_TO_EVERYTHING = (): boolean => true;

/**
 * The entries this session may see, in table order.
 *
 * Both the sidebar and the palette's Pages group call this rather than writing
 * the gate expression themselves — two copies is two chances for a regroup to
 * widen one of them. It now takes BOTH gates for the same reason: a surface
 * that filters on permission alone would show a packaged entry the API refuses.
 */
export function visibleNavItems(
  can: (resource: string, action: string) => boolean,
  entitled: (capability: string | undefined) => boolean = ENTITLED_TO_EVERYTHING,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem[] {
  return items.filter((item) => navItemVisible(item, can, entitled));
}

/**
 * A query of this length or less is treated as a WORD rather than an identifier.
 *
 * Above it the query is almost certainly a pasted address (42), transaction hash
 * (66) or id, where a page whose label happens to contain the substring is noise
 * in front of the record the caller actually pasted. See {@link matchNavPages}.
 */
export const NAV_PAGE_SHORT_QUERY_MAX = 12;

/** True when the query reads as a word — the case where pages outrank fuzzy data. */
export function isShortNavQuery(query: string): boolean {
  const text = query.trim();
  return text.length > 0 && text.length <= NAV_PAGE_SHORT_QUERY_MAX;
}

/** Why an entry matched — the palette shows the section, so the reason is visible. */
export type NavPageMatchOn = "label" | "section";

export interface NavPageMatch {
  item: NavItem;
  /** Higher is a better match. Relative only — see {@link matchNavPages}. */
  score: number;
  on: NavPageMatchOn;
}

/** Score bands, best first. A label beats a section; whole beats prefix beats substring. */
const LABEL_EXACT = 100;
const LABEL_PREFIX = 80;
const LABEL_WORD_PREFIX = 70;
const LABEL_SUBSTRING = 60;
const SECTION_EXACT = 50;
const SECTION_PREFIX = 40;
const SECTION_SUBSTRING = 30;

/**
 * Rank nav entries against a typed query, on label and on section name.
 *
 * Section matching is what makes the regroup answerable from the keyboard:
 * typing "reference" or "automate" lists that whole section, so the names the
 * rail teaches are also the names the palette accepts.
 *
 * `items` is the caller's ALREADY-GATED list ({@link visibleNavItems}) — this
 * function does no permission work, so it can never widen a gate by accident.
 * A blank query matches nothing (the palette shows recents there instead).
 */
export function matchNavPages(
  query: string,
  items: readonly NavItem[] = NAV_ITEMS,
): NavPageMatch[] {
  const text = query.trim().toLowerCase();
  if (!text) return [];

  const matches: NavPageMatch[] = [];
  for (const item of items) {
    const label = item.label.toLowerCase();
    const section = item.section?.toLowerCase() ?? "";

    if (label === text) matches.push({ item, score: LABEL_EXACT, on: "label" });
    else if (label.startsWith(text)) matches.push({ item, score: LABEL_PREFIX, on: "label" });
    else if (label.split(/[^a-z0-9]+/).some((word) => word.length > 0 && word.startsWith(text)))
      matches.push({ item, score: LABEL_WORD_PREFIX, on: "label" });
    else if (label.includes(text)) matches.push({ item, score: LABEL_SUBSTRING, on: "label" });
    else if (section === text) matches.push({ item, score: SECTION_EXACT, on: "section" });
    else if (section.startsWith(text)) matches.push({ item, score: SECTION_PREFIX, on: "section" });
    else if (section.includes(text))
      matches.push({ item, score: SECTION_SUBSTRING, on: "section" });
  }

  // Stable within a band: the table's own order, which is the rail's order, so
  // two equally-good matches arrive in the order the reader has been taught.
  return matches
    .map((match, index) => ({ match, index }))
    .sort((a, b) => b.match.score - a.match.score || a.index - b.index)
    .map(({ match }) => match);
}
